import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSLUGName } from '../../hooks/useHistory';
import { RESTClient } from '../client';
import { ANALYTICS_API_BASE_URL } from '../../env';

const GET_DASHBOARD_DELAY_METRICS = 'DASHBOARD_DELAY_METRICS';

type DelayMetricsResponse = {
  means: Record<string, Record<string, number | null> | number | null>;
  sds: Record<string, Record<string, number | null> | number | null> | null;
  counts: Record<string, any> | null;
};

export type DelayMetricsData = {
  fcots: {
    onTimePercent: number | null;
    avgDelayMin: number | null;
    sdDelayMin: number | null;
    count: number | null;
  };
  lcote: {
    endDelayPercent: number | null;
    avgDelayMin: number | null;
    sdDelayMin: number | null;
    count: number | null;
  };
};

const extractFirst = (
  obj: Record<string, Record<string, number | null> | number | null> | null | undefined,
  key: string,
): number | null => {
  if (!obj) return null;
  const entry = obj[key];
  if (entry === null || entry === undefined) return null;
  if (typeof entry === 'number') return entry;
  const values = Object.values(entry as Record<string, number | null>).filter((v) => v !== null);
  return values.length > 0 ? (values[0] as number) : null;
};

const fetchDelayMetrics = async (params: Record<string, any>): Promise<DelayMetricsResponse> => {
  const { data } = await RESTClient.request<DelayMetricsResponse>({
    baseURL: ANALYTICS_API_BASE_URL + '/api/v1/delay_metrics',
    params,
  });
  return data;
};

export const useQueryDashboardDelayMetrics = (variables: { from: string; to: string }) => {
  const organization = useSLUGName();

  const baseParams = useMemo(
    () => ({
      from: variables.from,
      to: variables.to,
      ...(organization ? { organization } : {}),
    }),
    [variables.from, variables.to, organization],
  );

  const ratioParams = useMemo(
    () => ({ ...baseParams, delay_ratios: 'true', grace_period: 300 }),
    [baseParams],
  );
  const avgParams = useMemo(
    () => ({ ...baseParams, delay_ratios: 'false', grace_period: 300 }),
    [baseParams],
  );

  const fetchRatios = useCallback(() => fetchDelayMetrics(ratioParams), [ratioParams]);
  const fetchAvgs = useCallback(() => fetchDelayMetrics(avgParams), [avgParams]);

  const { data: ratioData, isLoading: ratioLoading } = useQuery(
    [GET_DASHBOARD_DELAY_METRICS, 'ratios', ratioParams],
    fetchRatios,
    { retry: 1 },
  );
  const { data: avgData, isLoading: avgLoading } = useQuery(
    [GET_DASHBOARD_DELAY_METRICS, 'avgs', avgParams],
    fetchAvgs,
    { retry: 1 },
  );

  const data: DelayMetricsData = useMemo(() => {
    const fcDelayRatio = extractFirst(ratioData?.means, 'first_case_delay');
    const lcDelayRatio = extractFirst(ratioData?.means, 'last_case_delay');
    const fcAvg = extractFirst(avgData?.means, 'first_case_delay');
    const lcAvg = extractFirst(avgData?.means, 'last_case_delay');
    const fcSd = extractFirst(avgData?.sds ?? null, 'first_case_delay');
    const lcSd = extractFirst(avgData?.sds ?? null, 'last_case_delay');
    const fcCount = extractFirst(avgData?.counts ?? null, 'first_case_delay');
    const lcCount = extractFirst(avgData?.counts ?? null, 'last_case_delay');

    return {
      fcots: {
        onTimePercent: fcDelayRatio !== null ? Math.round(100 - fcDelayRatio) : null,
        avgDelayMin: fcAvg !== null ? Math.round(fcAvg) : null,
        sdDelayMin: fcSd !== null ? Math.round(Math.abs(fcSd)) : null,
        count: fcCount,
      },
      lcote: {
        endDelayPercent: lcDelayRatio !== null ? Math.round(lcDelayRatio) : null,
        avgDelayMin: lcAvg !== null ? Math.round(lcAvg) : null,
        sdDelayMin: lcSd !== null ? Math.round(Math.abs(lcSd)) : null,
        count: lcCount,
      },
    };
  }, [ratioData, avgData]);

  return { data, loading: ratioLoading || avgLoading };
};
